"use client";

import Navigation from "@/HomeFeatures/Navigation";
import { gdprComplianceData } from "./data";
import DOMPurify from "isomorphic-dompurify";

export default function GdprCompliance() {
  return (
    <>
      <section>
        {/* Page Hero */}
        <div className="relative z-10 w-full">
          <div className="w-full h-full bg-white bg-cover bg-center flex flex-col">
            <Navigation dark={false} />
            <div className="w-full m-auto mt-[60px] md:mt-[80px] mb-[20px] md:mb-[40px] px-4 max-w-[1400px] md:px-8">
              <h5 className="self-stretch text-center justify-start text-[#636363] text-[clamp(0.875rem,0.4664rem+0.9815vw,2rem)] font-normal tracking-tight">
                GDPR Compliance
              </h5>
              <h1 className="self-stretch mx-auto text-center max-w-[23ch] justify-start text-black py-[15px] md:py-[20px] text-[clamp(1.75rem,4vw,var(--service-banner-title))] font-normal  clash-font capitalize leading-[110%] md:leading-[100%]">
                What is <span className="text-[#225BD4]">GDPR?</span>
              </h1>
              <p className="self-stretch text-center justify-start text-[#636363] text-[clamp(0.875rem,0.4664rem+0.9815vw,2rem)] font-normal tracking-tight">
                The General Data Protection Regulation (GDPR) is a European
                Union regulation designed to protect individuals’ personal data
                and strengthen their rights over how that data is collected,
                used, stored, and shared. It applies to the processing of
                personal data by organisations within the EU and, in certain
                circumstances, to organisations outside the EU that offer goods
                or services to individuals in the EU or monitor their behaviour.
              </p>
            </div>
          </div>
        </div>

        {/* Introduction Section */}
        <div className="w-full bg-white px-4 sm:px-8 md:px-12 lg:px-[100px] py-[40px] sm:py-[60px] md:py-[80px] lg:py-[100px] flex flex-col gap-4 md:gap-6">
          <p className="text-[#00040b] text-[clamp(1.25rem,3vw,2.125rem)] font-medium clash-font leading-[130%] md:leading-[42px]">
            {gdprComplianceData.introduction.introText}
          </p>

          <p className="text-[#00040b] text-[clamp(1rem,2vw,1.25rem)] font-medium italic leading-[140%] md:leading-[28px]">
            {gdprComplianceData.introduction.policyText}
          </p>
        </div>

        {/* Content Sections */}
        <div className="w-full bg-white px-4 sm:px-8 md:px-12 lg:px-[100px] pb-[40px] sm:pb-[60px] md:pb-[80px] lg:pb-[100px] flex flex-col items-center gap-[20px] md:gap-[30px]">
          {gdprComplianceData.sections?.map((section, index) => (
            <div key={index} className="w-full max-w-[1720px]">
              <div className="flex flex-col gap-[8px] md:gap-[10px]">
                <h2 className="text-[#00040b] text-[clamp(1.125rem,2.5vw,2rem)] font-medium clash-font leading-[130%] md:leading-[36px]">
                  {section.title}
                </h2>

                {section.intro && (
                  <p className="text-[#636364] text-[clamp(0.9375rem,2vw,1.25rem)] font-normal leading-[140%] md:leading-[28px]">
                    {section.intro}
                  </p>
                )}

                {section.bullets && (
                  <ul className="list-disc pl-4 md:pl-5 space-y-1 md:space-y-2">
                    {section.bullets.map((bullet, bulletIndex) => (
                      <li
                        key={bulletIndex}
                        className="text-[#636364] text-[clamp(0.9375rem,2vw,1.25rem)] font-normal leading-[140%] md:leading-[28px]"
                        dangerouslySetInnerHTML={{
                          __html: DOMPurify.sanitize(bullet, {
                            ALLOWED_TAGS: ["b", "strong", "br"],
                          }),
                        }}
                      />
                    ))}
                  </ul>
                )}

                {section.content && !section.intro && (
                  <p className="text-[#636364] text-[clamp(0.9375rem,2vw,1.25rem)] font-normal leading-[140%] md:leading-[28px] whitespace-pre-line">
                    {section.content}
                  </p>
                )}

                {section.subsections?.map((subsection, subIndex) => (
                  <div key={subIndex} className="mt-[15px] md:mt-[20px]">
                    <h3 className="text-[#00040b] text-[clamp(1.0625rem,2.2vw,1.75rem)] font-medium clash-font leading-[130%] md:leading-[36px] mb-[8px] md:mb-[10px]">
                      {subsection.title}
                    </h3>

                    {subsection.intro && (
                      <p className="text-[#636364] text-[clamp(0.9375rem,2vw,1.25rem)] font-normal leading-[140%] md:leading-[28px] mb-2">
                        <span
                          dangerouslySetInnerHTML={{
                            __html: DOMPurify.sanitize(subsection.intro || "", {
                              ALLOWED_TAGS: ["b", "strong", "br"],
                            }),
                          }}
                        />
                      </p>
                    )}

                    {subsection.bullets && (
                      <ul className="list-disc pl-4 md:pl-5 space-y-1 md:space-y-2">
                        {subsection.bullets.map((bullet, bIndex) => (
                          <li
                            key={bIndex}
                            className="text-[#636364] text-[clamp(0.9375rem,2vw,1.25rem)] font-normal leading-[140%] md:leading-[28px]"
                            dangerouslySetInnerHTML={{
                              __html: DOMPurify.sanitize(bullet, {
                                ALLOWED_TAGS: ["b", "strong", "br"],
                              }),
                            }}
                          />
                        ))}
                      </ul>
                    )}
                  </div>
                ))}
              </div>

              {index < gdprComplianceData.sections.length - 1 && (
                <div className="w-full h-[1px] bg-[#ecf0f6] mt-[20px] md:mt-[30px]" />
              )}
            </div>
          ))}
        </div>
      </section>
    </>
  );
}
